import React, {useEffect} from "react";
import styled from "styled-components";
import BasePage from "./BasePage";
import BrandProductsBrowser from "@menu/components/BrandProductsList";
import useBrand from "src/menu/hooks/useBrand";
import {PageHead} from "src/core/seo/components/Head";
import useRouter from "src/core/common/hooks/useRouter";
import BrandDetailSchema from "src/core/seo/components/BrandDetailSchema";
import FiltersPanel from "src/themes/flight/components/FiltersPanel";
import Title from "src/core/common/components/modules/Title";
import ActionsSortFilter from "src/themes/flight/elements/filters/ActionsSortFilter";
import useAppliedFilters from "src/core/common/hooks/useAppliedFilters";
import useBreadcrumbs from "src/core/common/hooks/useBreadcrumbs";
import useAdBanner from "src/core/common/hooks/useAdBanner";
import dynamic from "next/dynamic";
import {logModuleLoadError} from "src/core/common/utils";

const AdBanner = dynamic(() =>
  import("@themes/default/components/AdBanner").catch(logModuleLoadError("adBanner"))
);

export default function FlightBrandPage(props) {
  const router = useRouter();
  const brandSlug = router.query["brand"];
  const [brand] = useBrand(brandSlug, ["brandSlug"]);
  const [filters] = useAppliedFilters();
  const {showAdBanner} = useAdBanner();
  const [, setCategory, , setBrand, setSearch] = useBreadcrumbs();

  const brandDescription = () => ({
    __html: brand && brand.getDescription(),
  });

  useEffect(() => {
    setSearch(null);
  }, []);

  useEffect(() => {
    if (brand) {
      setBrand(brand);
      setCategory();
    }
    // eslint-disable-next-line
  }, [brand]);

  return (
    <BasePage {...props}>
      {showAdBanner && <AdBanner />}
      <Container>
        <div className={"title"}>
          {brand && (
            <PageHead
              title={brand.getName()}
              description={brand.getDescription()}
              image={brand.getLogo()}
              schemas={<BrandDetailSchema brand={brand} />}
            />
          )}
          {brand && (
            <Title styles={{fontSize: {lg: "52px", md: "52px", sm: "32px"}}}>
              {brand.getName()}
            </Title>
          )}
          <Description dangerouslySetInnerHTML={brandDescription()} />
        </div>
        <div className={"filters"}>
          <FiltersPanel displayOptions={{brandsVisible: false}} />
        </div>
        <div className={"mobile-filters"}>
          <ActionsSortFilter
            filters={filters}
            filtersDisplayOptions={{brandsVisible: false}}
          />
        </div>
        <div className={"browser"}>
          <BrandProductsBrowser />
        </div>
      </Container>
    </BasePage>
  );
}

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
  box-sizing: border-box;
  padding: 0 32px;
  display: grid;
  grid-template-columns: 200px 1fr;
  grid-template-rows: auto 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 0;

  .title {
    grid-area: 1 / 1 / 2 / 6;
    display: flex;
    flex-direction: column;
    align-items: start;
    justify-content: start;
    padding: ${({theme}) => theme.v2.spacing("m")} 0;
  }

  .filters {
    @media (min-width: 768px) {
      padding-top: 20px;
    }
    grid-area: 2 / 1 / 4 / 2;
  }

  .browser {
    grid-area: 2 / 2 / 4 / 6;
  }

  @media (max-width: 1023px) {
    padding: 0 16px;
  }

  @media (max-width: 767px) {
    display: flex;
    flex-direction: column;
    .filters {
      display: none;
    }

    .product-card__header-container,
    .product-card__skeleton {
      margin: 0 auto;
    }

    .product-card__skeleton {
      display: block;
    }
  }

  @media (min-width: 768px) {
    .mobile-filters {
      display: none;
    }
  }
`;

const Description = styled.div``;
